import React, { useState, useEffect } from 'react';
import {
    Search, Download, Printer, ArrowLeft,
    FileText, Wallet, Calendar, User, MessageCircle, Mail, Loader2, CheckCircle
} from 'lucide-react';
import api from '../api/axiosInstance';

const ClientStatement = () => {
    const [clients, setClients] = useState([]);
    const [selectedClient, setSelectedClient] = useState('');
    const [ledger, setLedger] = useState([]);
    const [loading, setLoading] = useState(false);
    const [exportLoading, setExportLoading] = useState(false);
    const [clientData, setClientData] = useState(null);
    const [balance, setBalance] = useState(0);

    useEffect(() => {
        api.get('get_directory.php?type=client').then(res => setClients(res.data.data));
    }, []);

    useEffect(() => {
        if (selectedClient) {
            setLoading(true);
            api.get(`get_client_statement.php?client_id=${selectedClient}`)
                .then(res => {
                    const data = res.data.data || [];
                    setLedger(data);
                    const total = data.reduce((acc, row) => acc + (parseFloat(row.debit) - parseFloat(row.credit)), 0);
                    setBalance(total);
                    setClientData(clients.find(c => c.id == selectedClient));
                    setLoading(false);
                });
        } else {
            setLedger([]);
            setBalance(0);
            setClientData(null);
        }
    }, [selectedClient, clients]);

    const sendWhatsAppReminder = () => {
        console.log("Client Data:", clientData);
        if (!clientData?.phone) return alert("No phone number found!");
        const text = `*Payment Reminder: Laxman Interior*%0A%0AHello *${clientData.name}*,%0A%0AOutstanding Balance: *₹${balance.toLocaleString()}*.%0A%0AKindly clear the dues for continued site progress.%0A%0A_Statement Generated via ClientOrbit IMOS_`;
        window.open(`https://wa.me/91${clientData.phone}?text=${text}`, '_blank');
    };

    const exportStatementPDF = async () => {
        if (!selectedClient) return;
        setExportLoading(true);
        try {
            const response = await api.get(`generate_statement_pdf.php?client_id=${selectedClient}`, {
                responseType: 'blob'
            });
            const url = window.URL.createObjectURL(new Blob([response.data]));
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', `Statement_${clientData.name.replace(/\s+/g, '_')}.pdf`);
            document.body.appendChild(link);
            link.click();
            link.remove();
        } catch (err) {
            alert("Export failed");
        } finally {
            setExportLoading(false);
        }
    };

    let runningBalance = 0;
    const ledgerWithBalance = ledger.map(item => {
        runningBalance += (parseFloat(item.debit) - parseFloat(item.credit));
        return { ...item, balance: runningBalance };
    });

    return (
        <div className="space-y-8 p-4">
            {/* Header */}
            <div className="bg-white p-8 rounded-[40px] border border-slate-100 shadow-sm flex flex-col xl:flex-row justify-between items-center gap-6">
                <div className="flex items-center gap-4 w-full xl:w-1/3">
                    <div className="p-4 bg-indigo-50 text-indigo-600 rounded-2xl"><User size={24} /></div>
                    <div className="flex-1">
                        <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest">Client Statement</p>
                        <select className="bg-transparent border-none font-black text-xl outline-none w-full" value={selectedClient} onChange={(e) => setSelectedClient(e.target.value)}>
                            <option value="">Choose a Client...</option>
                            {clients.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                        </select>
                    </div>
                </div>

                <div className="flex flex-wrap gap-3 w-full xl:w-auto justify-end">
                    {selectedClient && (
                        <>
                            <button onClick={sendWhatsAppReminder} className="bg-emerald-600 hover:bg-emerald-700 text-white px-6 py-4 rounded-2xl font-black flex items-center gap-2 shadow-lg transition-all text-xs uppercase"><MessageCircle size={18} /> Reminder</button>
                            <button disabled={exportLoading} onClick={exportStatementPDF} className="bg-slate-900 text-white px-8 py-4 rounded-2xl font-bold shadow-xl flex items-center gap-2 text-xs uppercase">
                                {exportLoading ? <Loader2 className="animate-spin" size={18} /> : <Download size={18} />} Export Statement
                            </button>
                        </>
                    )}
                </div>
            </div>

            {selectedClient ? (
                <div className="space-y-6 animate-in fade-in duration-500">
                    {/* Summary Cards */}
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                        <div className="bg-white p-8 rounded-[40px] border border-slate-100">
                            <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest mb-2">Invoiced Amount</p>
                            <h3 className="text-3xl font-black text-slate-900">₹{ledger.reduce((sum, i) => sum + parseFloat(i.debit), 0).toLocaleString()}</h3>
                        </div>
                        <div className="bg-white p-8 rounded-[40px] border border-slate-100">
                            <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest mb-2">Net Cash Received</p>
                            <h3 className="text-3xl font-black text-emerald-600">₹{ledger.reduce((sum, i) => sum + parseFloat(i.credit), 0).toLocaleString()}</h3>
                        </div>
                        <div className={`p-8 rounded-[40px] text-white shadow-xl transition-all ${balance > 0 ? 'bg-rose-600 shadow-rose-100' : 'bg-emerald-600 shadow-emerald-100'}`}>
                            <p className="text-[10px] font-black opacity-60 uppercase tracking-widest mb-2">Final Outstanding</p>
                            <h3 className="text-3xl font-black">₹{balance.toLocaleString()}</h3>
                        </div>
                    </div>

                    {/* Table */}
                    <div className="bg-white rounded-[40px] border border-slate-100 shadow-sm overflow-hidden">
                        <table className="w-full text-left">
                            <thead className="bg-slate-50 text-[9px] font-black text-slate-400 uppercase tracking-[0.2em] border-b border-slate-100">
                                <tr>
                                    <th className="p-6">Date / Ref</th>
                                    <th className="p-6">Tax Breakup (GST / TDS)</th>
                                    <th className="p-6 text-right">Debit (+)</th>
                                    <th className="p-6 text-right">Credit (-)</th>
                                    <th className="p-6 text-right">Balance</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-slate-50">
                                {loading ? (
                                    <tr><td colSpan="5" className="p-20 text-center font-black text-slate-200 text-2xl uppercase animate-pulse">Computing Accounts...</td></tr>
                                ) : ledgerWithBalance.map((row, idx) => (

                                    <tr key={idx} className="hover:bg-slate-50/50 transition-all group">
                                        <td className="p-6">
                                            <p className="text-xs font-bold text-slate-900 uppercase tracking-tighter">{row.type} #{row.ref}</p>
                                            <p className="text-[9px] font-black text-slate-400 mt-1">{row.t_date}</p>
                                        </td>
                                        <td className="p-6">
                                            <div className="flex flex-wrap gap-2">
                                                {/* Logic check: Ensure numeric values are parsed correctly */}
                                                {parseFloat(row.gst_amt) > 0 && (
                                                    <span className="text-[8px] font-black bg-indigo-50 text-indigo-600 px-2 py-1 rounded-md border border-indigo-100 uppercase">
                                                        GST: ₹{parseFloat(row.gst_amt).toLocaleString()}
                                                    </span>
                                                )}
                                                {parseFloat(row.tds_amt) > 0 && (
                                                    <span className="text-[8px] font-black bg-rose-50 text-rose-600 px-2 py-1 rounded-md border border-rose-100 uppercase">
                                                        TDS: ₹{parseFloat(row.tds_amt).toLocaleString()}
                                                    </span>
                                                )}
                                                {/* Fallback if both are zero/null */}
                                                {!(parseFloat(row.gst_amt) > 0 || parseFloat(row.tds_amt) > 0) && (
                                                    <span className="text-[8px] text-slate-300 font-bold italic">Standard Entry</span>
                                                )}
                                            </div>
                                        </td>
                                        <td className="p-6 text-right font-bold text-slate-900">
                                            {parseFloat(row.debit) > 0 ? `₹${parseFloat(row.debit).toLocaleString()}` : '--'}
                                        </td>
                                        <td className="p-6 text-right font-bold text-emerald-600">
                                            {parseFloat(row.credit) > 0 ? `₹${parseFloat(row.credit).toLocaleString()}` : '--'}
                                        </td>
                                        <td className="p-6 text-right font-black text-slate-900 bg-slate-50/30 group-hover:bg-indigo-50/30">
                                            ₹{parseFloat(row.balance).toLocaleString()}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>
            ) : (
                <div className="h-96 border-4 border-dashed border-slate-100 rounded-[40px] flex flex-col items-center justify-center text-slate-300">
                    <FileText size={64} className="mb-4 opacity-10" />
                    <p className="font-black italic text-lg text-slate-400 uppercase tracking-widest">Select Client to Load Statement</p>
                </div>
            )}
        </div>
    );
};

export default ClientStatement;